import { useState, useEffect, useRef } from 'react';
import { usePersistence } from './usePersistence';
import api from '../services/api';
import toast from '../utils/toast';
import { toLocalISODate, getTodayInSingaporeTimezone, formatDate, formatDateTimeCompact } from '../utils/dateUtils';
import { computeEmploymentWindow } from './employmentWindow';
import { isDayLockableNow, getLockBlockReason } from './lockableDay';
import { rosterPayloadBelongsToSelection } from './rosterLoadGuard';

// ── Module-level Utilities ───────────────────────────────────────────────────

export const isEmployeeFullyLocked = (employee, dailyRecords) => {
  if (!dailyRecords || dailyRecords.length === 0) return false;
  return dailyRecords.every(rec => rec.lock_status === 'locked');
};

// Re-exported for backward compatibility with any external import paths
// that previously read this helper from useAdjustAndLock.js.
export { computeEmploymentWindow };

const isRecordComplete = (rec) => {
  if (rec.day_type !== 'working') return true;
  return rec.adjusted_clock_in && rec.adjusted_clock_out && rec.total_hours > 0 && rec.sites && rec.sites.length > 0;
};

export const canLockEmployee = (dailyRecords) => {
  if (!dailyRecords || dailyRecords.length === 0) return false;
  return dailyRecords.every(rec => isRecordComplete(rec));
};

const formatDateCount = (count) => `${count} date${count === 1 ? '' : 's'}`;

/**
 * One outcome toast for a bulk lock/unlock. Partial failure is a warning,
 * not a success — HR needs to notice the miss, not a green "locked" pill.
 */
const toastBulkDateResult = ({
  successCount,
  failCount,
  autoLocked = false,
  action = 'lock',
}) => {
  const id = action === 'unlock' ? 'bulk-unlock-result' : 'bulk-lock-result';
  const verb = action === 'unlock' ? 'unlock' : 'lock';
  const verbPast = action === 'unlock' ? 'Unlocked' : 'Locked';
  if (successCount === 0) {
    toast.error(`Failed to ${verb} ${formatDateCount(failCount)}`, { id });
    return;
  }
  const base = `${verbPast} ${formatDateCount(successCount)}`;
  if (failCount > 0) {
    toast.warning(`${base}, ${failCount} failed`, { id });
    return;
  }
  if (autoLocked) {
    toast.success(`${base} — employee locked for this period`, { id });
    return;
  }
  toast.success(base, { id });
};

/**
 * Shared hook for attendance adjustment and lock management.
 * Used by PayrollProcessingTab (Step 2) and FinalSettlementsTab (Step 1).
 *
 * @param {Object} config
 * @param {string} config.period - Period in YYYY-MM format
 * @param {string[]} config.departmentIds - Array of department UUIDs; empty array = all departments
 * @param {boolean} [config.enabled=true] - When true, auto-load / editing effects fire
 * @param {string} [config.persistencePrefix='adjust-lock'] - Prefix for localStorage keys
 * @param {string[]} [config.staffIdFilter] - If provided, only these staff IDs appear in the employee list
 * @param {Object} [config.employmentEndDates={}] - Map of staff_id → 'YYYY-MM-DD' for post-employment date handling
 */
export const useAdjustAndLock = ({
  period,
  departmentIds = [],
  enabled = true,
  persistencePrefix = 'adjust-lock',
  staffIdFilter = null,
  employmentEndDates = {},
}) => {
  // Normalise: legacy callers may pass departmentId (string) instead of departmentIds (array)
  const _deptIds = Array.isArray(departmentIds)
    ? departmentIds.filter(id => id && id !== 'all')
    : (departmentIds && departmentIds !== 'all' ? [departmentIds] : []);
  // ── Attendance & Lock State ────────────────────────────────────────────────

  const [exceptionsPending, setExceptionsPending] = useState(null);
  const [locksSummary, setLocksSummary] = useState({ locked: 0, total: 0 });
  const [globalLocksSummary, setGlobalLocksSummary] = useState({ locked: 0, total: 0 });
  const [processingReadiness, setProcessingReadiness] = useState(null);
  const [adjusting, setAdjusting] = useState(false);
  const [totalEmployees, setTotalEmployees] = useState(0);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [locksLoading, setLocksLoading] = useState(false);
  const [locksByStaffId, setLocksByStaffId] = useState({});
  const [adjustedReady, setAdjustedReady] = useState(false);

  // ── Master-Detail State ────────────────────────────────────────────────────

  const [employeeList, setEmployeeList] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeDailyRecords, setEmployeeDailyRecords] = useState([]);
  const [rowLocking, setRowLocking] = useState(new Set());
  const [rowRenormalizing, setRowRenormalizing] = useState(new Set());
  const [employeeLocking, setEmployeeLocking] = useState(new Set());
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
  const [filterClassification, setFilterClassification] = useState('all');
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [lastSelectedIndex, setLastSelectedIndex] = useState(null);

  // ── Progress Modal ─────────────────────────────────────────────────────────

  const [progressModal, setProgressModal] = useState({
    open: false,
    currentPhase: 0,
    progress: null,
    statusMessage: '',
    error: null,
    department: null,
    currentEmployee: null,
    subPhase: null,
  });
  const ADJUST_PHASES = [
    { label: 'Normalizing attendance data' },
    { label: 'Fetching summary data' },
    { label: 'Loading employee details' },
    { label: 'Finalizing' },
  ];

  // ── Unsaved Edits (persisted) ──────────────────────────────────────────────

  const [allEditedValues, setAllEditedValues] = usePersistence(
    `${persistencePrefix}-all-edits`, {}
  );

  // ── Selected Employee (persisted per period+department context) ────────────
  // HR works one employee at a time and expects a page refresh (or a bounce
  // back to this tab) to land them back on the SAME employee, not reset to the
  // top of the list. We persist only the staff_id, keyed by the period+dept
  // context (same context-map shape as `allEditedValues`), and re-resolve it
  // against the freshly-built employeeList on load so the restored object is
  // always current (never a stale snapshot). Each department remembers its own
  // last-viewed employee.
  const [selectedStaffByContext, setSelectedStaffByContext] = usePersistence(
    `${persistencePrefix}-selected-staff`, {}
  );

  // ── Conflict Detection ─────────────────────────────────────────────────────

  const [conflictWarning, setConflictWarning] = useState(null);

  // ── Cell Editing ───────────────────────────────────────────────────────────

  const [editingCell, setEditingCell] = useState(null);
  const [cellValue, setCellValue] = useState('');
  const [savingCell, setSavingCell] = useState(false);

  // ── Site Editing ───────────────────────────────────────────────────────────

  const [workSites, setWorkSites] = useState([]);
  const [siteFrequencyMap, setSiteFrequencyMap] = useState({});
  const [editingSiteCell, setEditingSiteCell] = useState(null);
  const [siteSearchTerm, setSiteSearchTerm] = useState('');
  const [loadingWorkSites, setLoadingWorkSites] = useState(false);

  // ── Confirmation Modal ─────────────────────────────────────────────────────

  const [confirmAction, setConfirmAction] = useState(null);

  // ── Unlocked Dates Map ─────────────────────────────────────────────────────
  // staff_id -> date string[] for dates unlocked during normalization

  const [unlockedDatesMap, setUnlockedDatesMap] = useState({});

  // ── Refs ────────────────────────────────────────────────────────────────────

  const lastLoadedPeriodRef = useRef(null);
  const lockCountCacheRef = useRef({});
  const employeeLoadRequestRef = useRef(0);
  // Monotonic id for roster fetches. A department pill starts a new load
  // while the previous pill's attendance-records request can still be in
  // flight (the effect fetch is not awaited by the progress modal). Only
  // the latest id may write attendanceRecords / the name list. A late
  // write from the previous department is filtered down to nobody and the
  // left panel stays blank until a refresh.
  const rosterLoadGenRef = useRef(0);

  // ── Date Helpers ───────────────────────────────────────────────────────────

  const getMonthRange = (yyyyMm) => {
    const [y, m] = yyyyMm.split('-').map(Number);
    const start = new Date(y, m - 1, 1);
    const end = new Date(y, m, 0);
    return {
      start,
      end,
      startIso: toLocalISODate(start),
      endIso: toLocalISODate(end),
    };
  };

  const todayIso = getTodayInSingaporeTimezone();
  const yesterdayIso = (() => {
    const [y, m, d] = todayIso.split('-').map(Number);
    const dt = new Date(y, m - 1, d - 1);
    return toLocalISODate(dt);
  })();

  const getLockableEndIso = (endIso) => {
    if (endIso < todayIso) return endIso;
    const todayRec = employeeDailyRecords.find(r => r.date === todayIso);
    if (todayRec && isDayLockableNow(todayRec, todayIso)) {
      return endIso > todayIso ? todayIso : endIso;
    }
    return yesterdayIso < endIso ? yesterdayIso : endIso;
  };

  /**
   * For gate checks (lock summary, exceptions), cap end_date at yesterday.
   * Working days cannot be locked until they conclude; today is only
   * lockable for approved full-day leave. Progress gates still use
   * yesterday so an unlocked "today" does not block the run.
   */
  const getEffectiveGateEnd = (monthEnd) => {
    const today = new Date(todayIso + 'T00:00:00');
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday < monthEnd ? yesterday : monthEnd;
  };

  // ── Formatting Helpers ─────────────────────────────────────────────────────

  const deriveFromSites = (sites, key, mode) => {
    if (!sites || sites.length === 0) return null;
    const values = sites.map((s) => s?.[key]).filter(Boolean);
    if (values.length === 0) return null;
    return mode === 'max'
      ? values.reduce((a, b) => (a > b ? a : b))
      : values.reduce((a, b) => (a < b ? a : b));
  };

  // DS-standard date formatters (re-exported under legacy names for PayrollManagement consumers)
  const formatDateDisplay = (dateStr) => (dateStr ? formatDate(dateStr) : '-');
  const formatDateTimeDisplay = (dateTimeStr) => (dateTimeStr ? formatDateTimeCompact(dateTimeStr) : '-');

  const formatPeriodRange = (periodStr) => {
    if (!periodStr) return '-';
    const [year, month] = periodStr.split('-').map(Number);
    const startDate = new Date(year, month - 1, 1);
    const endDate = new Date(year, month, 0);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${startDate.getDate()} ${months[startDate.getMonth()]} ${startDate.getFullYear()} to ${endDate.getDate()} ${months[endDate.getMonth()]} ${endDate.getFullYear()}`;
  };

  const normalizeTimeInput = (raw) => {
    if (!raw) return raw;
    const s = raw.trim().replace(/[^0-9:]/g, '');
    if (/^\d{1,2}:\d{2}$/.test(s)) {
      const [h, m] = s.split(':');
      return `${h.padStart(2, '0')}:${m}`;
    }
    if (/^\d{3}$/.test(s)) return `0${s[0]}:${s.slice(1)}`;
    if (/^\d{4}$/.test(s)) return `${s.slice(0, 2)}:${s.slice(2)}`;
    return s;
  };

  // ── Edit Context Helpers ───────────────────────────────────────────────────

  const getEditContextKey = () => {
    if (!period || !selectedEmployee) return null;
    const deptKey = _deptIds.length > 0 ? [..._deptIds].sort().join(',') : 'all';
    return `${period}-${deptKey}-${selectedEmployee.staff_id}`;
  };

  // Context key for the persisted employee SELECTION (no staff_id suffix — it
  // identifies the list HR is viewing, i.e. one selection per period+dept).
  const getSelectionContextKey = () => {
    if (!period) return null;
    const deptKey = _deptIds.length > 0 ? [..._deptIds].sort().join(',') : 'all';
    return `${period}-${deptKey}`;
  };

  // Given a freshly-built employee list, pick which employee should be
  // selected: the one HR last worked on in this context (persisted), else the
  // first. Returns null only for an empty list.
  const resolvePersistedSelection = (list) => {
    if (!list || list.length === 0) return null;
    const ctxKey = getSelectionContextKey();
    const desiredId = ctxKey ? selectedStaffByContext[ctxKey] : null;
    if (desiredId) {
      const match = list.find(emp => emp.staff_id === desiredId);
      if (match) return match;
    }
    return list[0];
  };

  const getCurrentContextEdits = () => {
    const contextKey = getEditContextKey();
    return contextKey ? (allEditedValues[contextKey] || {}) : {};
  };

  const setCurrentContextEdits = (edits) => {
    const contextKey = getEditContextKey();
    if (!contextKey) return;
    setAllEditedValues(prev => ({ ...prev, [contextKey]: edits }));
  };

  const hasUnsavedEdits = Object.keys(getCurrentContextEdits()).length > 0;

  const isRowEditable = (rec) => rec.isEditable && rec.lock_status !== 'locked';

  // ── Data Fetching ──────────────────────────────────────────────────────────

  // Build common query params with optional staff_ids scoping
  const buildScopeParams = (startDate, endDate, depts) => {
    const params = { start_date: startDate, end_date: endDate };
    const ids = Array.isArray(depts) ? depts.filter(id => id && id !== 'all') : [];
    if (staffIdFilter !== null && staffIdFilter !== undefined) {
      // staffIdFilter is active (non-null): use it exclusively even if empty
      // An empty array means "no matching staff" — send a sentinel that returns nothing
      if (staffIdFilter.length > 0) {
        params.staff_ids = staffIdFilter.join(',');
      } else {
        // No staff match the current filter; use an impossible staff ID sentinel
        params.staff_ids = '__no_match__';
      }
    } else if (ids.length > 0) {
      params.department_ids = ids.join(',');
    }
    return params;
  };

  const recheckExceptions = async (showToast = false) => {
    if (!period) return;
    const { start, end } = getMonthRange(period);
    const gateEnd = getEffectiveGateEnd(end);
    const loadingToast = showToast ? toast.loading('Checking exceptions...') : null;
    try {
      const params = buildScopeParams(toLocalISODate(start), toLocalISODate(gateEnd), _deptIds);
      const res = await api.get('/exceptions/summary/', { params });
      const pending = res.data?.pending_total ?? res.data?.pending ?? 0;
      setExceptionsPending(Number(pending));
      if (loadingToast) toast.dismiss(loadingToast);
      if (showToast) {
        pending === 0
          ? toast.success('No pending exceptions found')
          : toast.error(`${pending} exceptions need resolution`);
      }
    } catch (e) {
      if (loadingToast) toast.dismiss(loadingToast);
      if (showToast) toast.error('Failed to check exceptions');
      setExceptionsPending(null);
    }
  };

  const fetchLocksSummary = async (periodOverride = null, departmentOverride = null) => {
    const p = periodOverride || period;
    const d = departmentOverride !== null ? departmentOverride : _deptIds;
    if (!p) return;
    const { start, end } = getMonthRange(p);
    const gateEnd = getEffectiveGateEnd(end);
    try {
      const params = buildScopeParams(toLocalISODate(start), toLocalISODate(gateEnd), d);
      const res = await api.get('/attendance-lock/summary/', { params });
      setLocksSummary({ locked: res.data?.locked || 0, total: res.data?.total || 0 });
      setTotalEmployees(res.data?.total || 0);
    } catch (e) {
      setLocksSummary({ locked: 0, total: 0 });
      setTotalEmployees(0);
    }
  };

  const fetchGlobalLocksSummary = async (periodOverride = null) => {
    const p = periodOverride || period;
    if (!p) return;
    const { start, end } = getMonthRange(p);
    const gateEnd = getEffectiveGateEnd(end);
    try {
      const params = { start_date: toLocalISODate(start), end_date: toLocalISODate(gateEnd) };
      const res = await api.get('/attendance-lock/summary/', { params });
      setGlobalLocksSummary({ locked: res.data?.locked || 0, total: res.data?.total || 0 });
    } catch (e) {
      setGlobalLocksSummary({ locked: 0, total: 0 });
    }
  };

  const fetchProcessingReadiness = async (periodOverride = null) => {
    const p = periodOverride || period;
    if (!p) return;
    try {
      const res = await api.get('/confidential/payroll-runs/processing-readiness/', {
        params: { period: p },
      });
      setProcessingReadiness(res.data);
      const staffData = res.data?.staff || {};
      const workmenData = res.data?.workmen || {};
      const totalLocked = (staffData.locked_count || 0) + (workmenData.locked_count || 0);
      const totalCount = (staffData.total_count || 0) + (workmenData.total_count || 0);
      setGlobalLocksSummary({ locked: totalLocked, total: totalCount });
      const totalExceptions = (staffData.pending_exceptions || 0) + (workmenData.pending_exceptions || 0);
      setExceptionsPending(totalExceptions);
    } catch (e) {
      setProcessingReadiness(null);
    }
  };

  const fetchAttendanceLocks = async (periodOverride = null, departmentOverride = null) => {
    const gen = rosterLoadGenRef.current;
    const p = periodOverride || period;
    const d = departmentOverride !== null ? departmentOverride : _deptIds;
    if (!p) return {};
    const { start, end } = getMonthRange(p);
    try {
      setLocksLoading(true);
      const params = buildScopeParams(toLocalISODate(start), toLocalISODate(end), d);
      const res = await api.get('/attendance-lock/list/', { params });
      if (gen !== rosterLoadGenRef.current) return null;
      const locks = res.data?.locks || [];
      const byStaff = {};
      locks.forEach(l => { if (l.staff_id) byStaff[l.staff_id] = l.lock_status; });
      setLocksByStaffId(byStaff);
      return byStaff;
    } catch (e) {
      if (gen !== rosterLoadGenRef.current) return null;
      setLocksByStaffId({});
      return {};
    } finally {
      if (gen === rosterLoadGenRef.current) setLocksLoading(false);
    }
  };

  const fetchAttendanceRecords = async (periodOverride = null, departmentOverride = null) => {
    const gen = rosterLoadGenRef.current;
    const p = periodOverride || period;
    const d = departmentOverride !== null ? departmentOverride : _deptIds;
    if (!p) return [];
    const { start, end } = getMonthRange(p);
    try {
      setRecordsLoading(true);
      const params = buildScopeParams(toLocalISODate(start), toLocalISODate(end), d);
      const res = await api.get('/labour-cost/attendance-records/', { params });
      if (gen !== rosterLoadGenRef.current) return null;
      const records = res.data?.data?.records || res.data?.records || res.data?.results || [];
      setAttendanceRecords(records);
      return records;
    } catch (e) {
      if (gen !== rosterLoadGenRef.current) return null;
      setAttendanceRecords([]);
      return [];
    } finally {
      if (gen === rosterLoadGenRef.current) setRecordsLoading(false);
    }
  };

  // ── Lock Apply / Remove ────────────────────────────────────────────────────

  const handleApplyLock = async (staffId, reason = '') => {
    if (!period) return;
    const { startIso, endIso } = getMonthRange(period);
    try {
      await api.post('/attendance-lock/apply/', {
        staff_id: staffId, start_date: startIso, end_date: getLockableEndIso(endIso), notes: reason,
      });
      await Promise.all([fetchLocksSummary(), fetchGlobalLocksSummary(), fetchProcessingReadiness(), fetchAttendanceLocks(), buildEmployeeList()]);
      toast.success('Employee locked for this period', { id: 'attendance-lock-action' });
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to apply lock', { id: 'attendance-lock-action' });
    }
  };

  const handleRemoveLock = async (staffId, reason = '') => {
    if (!period) return;
    const { startIso, endIso } = getMonthRange(period);
    try {
      await api.post('/attendance-lock/remove/', {
        staff_id: staffId, start_date: startIso, end_date: endIso, notes: reason,
      });
      await Promise.all([fetchLocksSummary(), fetchGlobalLocksSummary(), fetchProcessingReadiness(), fetchAttendanceLocks(), buildEmployeeList()]);
      toast.success('Employee unlocked', { id: 'attendance-lock-action' });
    } catch (e) {
      toast.error('Failed to remove lock', { id: 'attendance-lock-action' });
    }
  };

  // ── Row Expansion ──────────────────────────────────────────────────────────

  const toggleRow = (dateKey) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(dateKey)) newExpanded.delete(dateKey);
    else newExpanded.add(dateKey);
    setExpandedRows(newExpanded);
  };

  // ── Build Employee List ────────────────────────────────────────────────────

  const buildEmployeeList = async (records = null, locks = null, gen = null) => {
    const loadGen = gen == null ? rosterLoadGenRef.current : gen;
    if (loadGen !== rosterLoadGenRef.current) return null;
    const recordsToUse = records !== null ? records : attendanceRecords;
    const locksToUse = locks !== null ? locks : locksByStaffId;
    // Late response from the department HR already left. Do not replace
    // the name list — the department filter would keep zero rows and the
    // panel would stay blank until refresh.
    if (!rosterPayloadBelongsToSelection(recordsToUse, _deptIds)) return null;
    if (!period || recordsToUse.length === 0) {
      if (loadGen !== rosterLoadGenRef.current) return null;
      setEmployeeList([]);
      return [];
    }
    const employeeMap = {};
    recordsToUse.forEach(rec => {
      if (_deptIds.length > 0 && !_deptIds.includes(rec.department_id)) return;
      if (!employeeMap[rec.staff_id]) {
        employeeMap[rec.staff_id] = {
          staff_id: rec.staff_id,
          staff_name: rec.staff_name || rec.staff_id,
          department_name: rec.department_name || 'N/A',
          classification: rec.classification || 'N/A',
          trade_code: rec.trade_code || '',
          employment_type: rec.employment_type || 'full_time',
          work_schedule_type: rec.work_schedule_type || null,
          is_probation: rec.is_probation || false,
          total_hours: 0,
          records: [],
        };
      }
      employeeMap[rec.staff_id].records.push(rec);
      employeeMap[rec.staff_id].total_hours += rec.total_hours || 0;
    });

    const empList = Object.values(employeeMap)
      .filter(emp => !staffIdFilter || staffIdFilter.includes(emp.staff_id))
      .map(emp => {
        const isSelected = selectedEmployee && emp.staff_id === selectedEmployee.staff_id;

        // ── Denominator: derive from the employment window ──────────────
        // Source of truth is the per-employee `employment_start_date` /
        // `employment_end_date` shipped by the records endpoint.  Falls
        // back to the legacy `employmentEndDates` prop for callers that
        // pre-compute the end date (Final Settlements path).
        const apiRow = emp.records[0] || {};
        const apiEffectiveDays = apiRow.effective_days_in_period;
        const empStartIso = apiRow.employment_start_date || null;
        const empEndIso = apiRow.employment_end_date || employmentEndDates[emp.staff_id] || null;

        const window = computeEmploymentWindow(period, empStartIso, empEndIso);
        // Prefer the server-computed denominator when present (single
        // source of truth); fall back to the local helper for older
        // payloads that don't yet include `effective_days_in_period`.
        const effectiveDays = (typeof apiEffectiveDays === 'number' && apiEffectiveDays >= 0)
          ? apiEffectiveDays
          : window.effectiveDays;

        // A record counts toward the numerator only if it sits within the
        // employment window.  Backend flags `is_pre_employment` /
        // `is_post_employment` are honoured first; date comparison is a
        // belt-and-braces guard for older API responses.
        const isWithinEmployment = (rec) => {
          if (rec.is_pre_employment || rec.is_post_employment) return false;
          if (empStartIso && rec.date && rec.date < empStartIso) return false;
          if (empEndIso && rec.date && rec.date > empEndIso) return false;
          return true;
        };

        const apiLockedCount = apiRow.locked_count;
        let lockedCount;
        if (isSelected && employeeDailyRecords.length > 0) {
          lockedCount = employeeDailyRecords.filter(
            rec => rec.lock_status === 'locked' && !rec.isPreEmployment && !rec.isPostEmployment
          ).length;
          lockCountCacheRef.current[emp.staff_id] = lockedCount;
        } else if (lockCountCacheRef.current[emp.staff_id] !== undefined) {
          lockedCount = lockCountCacheRef.current[emp.staff_id];
        } else if (apiLockedCount !== undefined) {
          // Backend already clamps to the employment window when serving
          // `effective_days_in_period`; safe to consume directly.
          lockedCount = apiLockedCount;
        } else {
          lockedCount = emp.records.filter(
            rec => rec.lock_status === 'locked' && isWithinEmployment(rec)
          ).length;
        }

        // Defensive clamp — the chip must never show N/M with N > M.
        lockedCount = Math.min(Math.max(lockedCount, 0), effectiveDays);

        return {
          ...emp,
          lock_status: effectiveDays > 0 && lockedCount >= effectiveDays ? 'locked' : 'unlocked',
          locked_count: lockedCount,
          total_days: effectiveDays,
          employment_start_date: empStartIso,
          employment_end_date: empEndIso,
          joined_mid_period: window.joinedMidPeriod,
          left_mid_period: window.leftMidPeriod,
          has_exceptions: false,
        };
      });

    if (Object.keys(employmentEndDates).length > 0) {
      empList.sort((a, b) => {
        const endA = employmentEndDates[a.staff_id] || '9999-12-31';
        const endB = employmentEndDates[b.staff_id] || '9999-12-31';
        return endA.localeCompare(endB);
      });
    }

    if (loadGen !== rosterLoadGenRef.current) return null;
    setEmployeeList(empList);
    return empList;
  };

  // ── Work Sites ─────────────────────────────────────────────────────────────

  const loadWorkSites = async () => {
    if (workSites.length > 0) return;
    try {
      setLoadingWorkSites(true);
      const response = await api.get('/work-sites/', { params: { page_size: 1000 } });
      const sitesData = Array.isArray(response.data)
        ? response.data
        : (response.data.results || []);
      const activeWorkSites = sitesData.filter(
        site => site.site_status === 'active' || site.site_status === undefined
      );
      setWorkSites(activeWorkSites);
    } catch (error) {
      console.error('Error loading work sites:', error);
      toast.error('Failed to load work site locations');
      setWorkSites([]);
    } finally {
      setLoadingWorkSites(false);
    }
  };

  // Site-frequency hint for the Sites dropdown badge ("Nx"). Counts how many
  // days the employee actually WORKED at each site within the current payroll
  // period (the month HR is processing on Step 2) — mirroring exactly what HR
  // sees in the table. Leave/OSL rows are excluded so the badge reflects
  // genuine on-site presence rather than prior leave attributions; the backend
  // already pre-fills the most-likely site for OSL days sandwiched between
  // matching sites. Derived from the already-loaded current-month records, so
  // it costs no extra network request.
  //
  // Keyed by ProjectWorkSite PK (`work_site_id`) wherever the backend can
  // resolve it — this is the only reliable signal. `site.site_name` is
  // frequently the PROJECT's name, not the work-site's name, for any
  // non-main-office code (see `_resolve_clock_in_project_label` in
  // staff_core/views.py), so it never matches `ProjectWorkSite.site_name` in
  // the dropdown list. `site.location` is closer (it carries the real
  // work-site name when one was selected) and is kept as a legacy fallback
  // for older session rows that pre-date the `work_site_id` payload field.
  const computeSiteFrequency = (records = []) => {
    // Count DISTINCT working DAYS per site, not raw sessions. A single day can
    // carry multiple work sessions for the same site (split shift, nightwork
    // cross-midnight split, a lunch-bracketed re-clock-in), which previously
    // inflated the badge by 1x per extra session on that day.
    const daysByKey = {};
    records.forEach(rec => {
      const day = rec.date;
      if (!day) return;
      rec.sites?.forEach(site => {
        if (site.is_leave_session) return;
        const key = site.work_site_id || site.location || site.site_name;
        if (!key) return;
        (daysByKey[key] || (daysByKey[key] = new Set())).add(day);
      });
    });
    const freq = {};
    Object.keys(daysByKey).forEach(key => { freq[key] = daysByKey[key].size; });
    return freq;
  };

  const getSiteFrequency = (site) =>
    siteFrequencyMap[site.id] || siteFrequencyMap[site.site_name] || 0;

  const getFilteredAndSortedSites = () => {
    if (!workSites || workSites.length === 0) return [];
    let filtered = workSites;
    if (siteSearchTerm) {
      const lowerSearch = siteSearchTerm.toLowerCase();
      filtered = workSites.filter(site =>
        site.site_name?.toLowerCase().includes(lowerSearch) ||
        site.site_address?.toLowerCase().includes(lowerSearch) ||
        site.project?.project_code?.toLowerCase().includes(lowerSearch) ||
        site.project?.project_name?.toLowerCase().includes(lowerSearch)
      );
    }
    return filtered.sort((a, b) => {
      const freqA = getSiteFrequency(a);
      const freqB = getSiteFrequency(b);
      if (freqB !== freqA) return freqB - freqA;
      return (a.site_name || '').toLowerCase().localeCompare((b.site_name || '').toLowerCase());
    });
  };

  // ── Load Employee Daily Records ────────────────────────────────────────────

  const loadEmployeeDailyRecords = async (employee) => {
    if (!employee || !period) {
      setEmployeeDailyRecords([]);
      return;
    }

    const requestId = ++employeeLoadRequestRef.current;

    try {
      const { startIso, endIso } = getMonthRange(period);
      const response = await api.get(
        `/labour-cost/attendance-calendar/?start_date=${startIso}&end_date=${endIso}&staff_id=${employee.staff_id}`
      );
      if (requestId !== employeeLoadRequestRef.current) return;

      if (response.data.success && response.data.data) {
        const dailyRecords = response.data.data.records || [];
        const empUnlockedDates = unlockedDatesMap[employee.staff_id] || [];
        const endDate = employmentEndDates[employee.staff_id];
        const enrichedRecords = dailyRecords.map(rec => {
          // Pre-employment: backend already strips synthesised PH/clock
          // data for these dates and sets `is_pre_employment`. Mirror
          // the post-employment treatment so they're rendered greyed-out
          // and excluded from selectable / count tallies.
          const isPreEmployment = !!rec.is_pre_employment;
          const isPostEmployment = rec.is_post_employment || (endDate ? rec.date > endDate : false);
          return {
            ...rec,
            isPreEmployment,
            isPostEmployment,
            // Attendance-exempt deemed-present rows are synthetic (no AR/session
            // backs them). They must stay read-only so an inline edit can't
            // trigger an AR write and break the deemed-present model — the
            // period is still lockable. See attendance-exempt-deemed-present.mdc.
            isEditable: rec.lock_status !== 'locked' && !isPreEmployment && !isPostEmployment && !rec.is_attendance_exempt,
            wasJustNormalized: empUnlockedDates.includes(rec.date),
          };
        });
        setEmployeeDailyRecords(enrichedRecords);

        await loadWorkSites();
        if (requestId !== employeeLoadRequestRef.current) return;
        setSiteFrequencyMap(computeSiteFrequency(dailyRecords));
        return enrichedRecords;
      } else {
        setEmployeeDailyRecords([]);
        return [];
      }
    } catch (error) {
      if (requestId !== employeeLoadRequestRef.current) return;
      console.error('Failed to load daily records:', error);
      toast.error('Failed to load daily attendance records');
      setEmployeeDailyRecords([]);
      return [];
    }
  };

  const selectEmployee = (employee) => {
    setSelectedEmployee(employee);
    loadEmployeeDailyRecords(employee);
    // Remember this choice so a refresh returns HR to the same employee.
    const ctxKey = getSelectionContextKey();
    if (ctxKey && employee?.staff_id) {
      setSelectedStaffByContext(prev => ({ ...prev, [ctxKey]: employee.staff_id }));
    }
  };

  // ── Cell Editing ───────────────────────────────────────────────────────────

  const handleCellClick = (date, fieldName, currentValue) => {
    const key = `${date}-${fieldName}`;
    setEditingCell(key);
    const contextEdits = getCurrentContextEdits();
    const savedEdit = contextEdits[key];
    setCellValue(savedEdit !== undefined ? savedEdit : (currentValue || ''));
  };

  const handleCellSave = async (record, fieldName) => {
    if (savingCell) return;
    let newValue = cellValue.trim();
    if (fieldName.includes('clock')) newValue = normalizeTimeInput(newValue);
    const oldValue = record[fieldName];
    const key = `${record.date}-${fieldName}`;

    if (fieldName === 'raw_clock_in' || fieldName === 'raw_clock_out') {
      toast.error('Actual clock times are device taps. Correct them on Attendance Daily or Realtime.');
      setEditingCell(null);
      return;
    }
    if (!isRowEditable(record)) {
      toast.error('Cannot save: This row is locked');
      setEditingCell(null);
      return;
    }
    if (
      (fieldName === 'adjusted_clock_in' || fieldName === 'adjusted_clock_out')
      && (!(record.attendance_record_id || record.id) || record.needs_normalization)
    ) {
      toast.error('Re-normalize this day before editing adjusted times');
      setEditingCell(null);
      return;
    }
    if (newValue === oldValue || (newValue === '' && !oldValue)) {
      setEditingCell(null);
      return;
    }
    if (fieldName.includes('clock')) {
      if (newValue !== '' && !/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(newValue)) {
        toast.error('Invalid time format. Use HH:MM (e.g. 1730)');
        return;
      }
      if (fieldName === 'adjusted_clock_in' || fieldName === 'adjusted_clock_out') {
        const toMinutes = (value) => {
          const [hour, minute] = String(value).split(':').map(Number);
          return (hour * 60) + minute;
        };
        const inTime = fieldName === 'adjusted_clock_in' ? newValue : record.adjusted_clock_in;
        const outTime = fieldName === 'adjusted_clock_out' ? newValue : record.adjusted_clock_out;
        if (inTime && outTime && toMinutes(outTime) <= toMinutes(inTime)) {
          toast.error('Adjusted out must be later than adjusted in on the same day');
          return;
        }
      }
    } else if (fieldName.includes('hours')) {
      if (newValue !== '' && (isNaN(newValue) || parseFloat(newValue) < 0)) {
        toast.error('Hours must be a positive number');
        return;
      }
    }

    const contextEdits = getCurrentContextEdits();
    setCurrentContextEdits({ ...contextEdits, [key]: newValue });

    setSavingCell(true);
    try {
      const recordId = record.attendance_record_id || record.id;
      if (!recordId) throw new Error('Missing attendance record id');

      const fieldMap = {
        raw_clock_in: 'attendance_raw_clock_in',
        raw_clock_out: 'attendance_raw_clock_out',
        adjusted_clock_in: 'attendance_clock_in',
        adjusted_clock_out: 'attendance_clock_out',
        lunch_break_hours: 'attendance_lunch_break_hours',
        dinner_break_hours: 'attendance_dinner_break_hours',
        weekday_ot_hours: 'attendance_weekday_ot_hours',
        rest_day_ot_hours: 'attendance_rest_day_ot_hours',
      };
      const apiField = fieldMap[fieldName] || fieldName;
      const isTimeField = [
        'attendance_raw_clock_in', 'attendance_raw_clock_out',
        'attendance_clock_in', 'attendance_clock_out',
      ].includes(apiField);
      let payloadValue = newValue;
      if (isTimeField && newValue) {
        payloadValue = `${record.date}T${newValue}:00+08:00`;
      } else if (apiField.endsWith('_hours') && newValue !== '') {
        payloadValue = parseFloat(newValue);
      }

      const payload = { [apiField]: payloadValue };
      if (record.attendance_updated_at) payload.expected_updated_at = record.attendance_updated_at;

      await api.patch(`/attendance/${recordId}/`, payload);

      const updatedEdits = { ...getCurrentContextEdits() };
      delete updatedEdits[key];
      setCurrentContextEdits(updatedEdits);
      toast.success('Updated');
      setEditingCell(null);
      await loadEmployeeDailyRecords(selectedEmployee);
    } catch (error) {
      if (error.response?.status === 409 || error.response?.data?.conflict) {
        toast.error('Record was modified by another user. Please refresh and try again.', { duration: 5000 });
        setConflictWarning({
          type: 'concurrent_edit',
          message: 'This record was modified by another user since you loaded it. Please refresh to see the latest data.',
          field: fieldName, yourValue: newValue,
          currentUpdatedAt: error.response?.data?.current_updated_at,
        });
        await loadEmployeeDailyRecords(selectedEmployee);
      } else if (error.response?.status === 403 || error.response?.data?.error?.includes('locked')) {
        toast.error('Cannot save: Row was locked by another admin');
        setConflictWarning({
          type: 'save_failed',
          message: 'Save failed because this row is locked. Please refresh to see current lock status.',
        });
        if (unlockedDatesMap[selectedEmployee?.staff_id]?.includes(record.date)) {
          setUnlockedDatesMap(prev => ({
            ...prev,
            [selectedEmployee.staff_id]: prev[selectedEmployee.staff_id].filter(d => d !== record.date),
          }));
        }
      } else {
        toast.error('Failed to update');
      }
      setCellValue(oldValue || '');
    } finally {
      setSavingCell(false);
    }
  };

  const handleSessionCellSave = async (record, site, fieldName) => {
    if (savingCell) return;
    let newValue = normalizeTimeInput(cellValue.trim());
    const oldValue = site[fieldName];
    if (!isRowEditable(record)) {
      toast.error('Cannot save: This row is locked');
      setEditingCell(null);
      return;
    }
    if (newValue === oldValue || (newValue === '' && !oldValue)) {
      setEditingCell(null);
      return;
    }
    if (newValue !== '' && !/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(newValue)) {
      toast.error('Invalid time format. Use HH:MM (e.g. 1730)');
      return;
    }
    setSavingCell(true);
    try {
      const recordId = record.attendance_record_id || record.id;
      const sessionId = site.id;
      if (!recordId || !sessionId) throw new Error('Missing record or session id');
      const apiFieldMap = { clock_in: 'start_time', clock_out: 'end_time' };
      const apiField = apiFieldMap[fieldName];
      if (!apiField) throw new Error(`Unsupported session field: ${fieldName}`);
      const isoValue = newValue ? `${record.date}T${newValue}:00+08:00` : null;
      await api.updateAttendanceSession(recordId, sessionId, { [apiField]: isoValue });
      toast.success('Updated');
      setEditingCell(null);
      await loadEmployeeDailyRecords(selectedEmployee);
    } catch (error) {
      console.error('Session save error:', error);
      toast.error('Failed to update session');
      setCellValue(oldValue || '');
    } finally {
      setSavingCell(false);
    }
  };

  // ── Site Editing ───────────────────────────────────────────────────────────

  const handleSiteClick = (date, sessionId = null) => {
    const key = sessionId ? `${date}-site-${sessionId}` : `${date}-site`;
    setEditingSiteCell(key);
    setSiteSearchTerm('');
  };

  const persistSiteOnRecord = async (rec, selectedSite, sessionIdOrIndex = null) => {
    let recordId = rec.attendance_record_id || rec.id;
    if (!recordId && rec.is_paid_leave) {
      await api.createLeaveRecord(selectedEmployee.staff_id, rec.date, selectedSite.id);
      return;
    }
    if (!recordId) {
      throw new Error('no-attendance-record');
    }
    let actualSessionId = null;
    if (sessionIdOrIndex !== null && sessionIdOrIndex !== undefined) {
      actualSessionId = sessionIdOrIndex;
    } else if (rec.sites && rec.sites.length === 1 && rec.sites[0].id) {
      actualSessionId = rec.sites[0].id;
    } else {
      const sessionsResponse = await api.getAttendanceSessions(recordId);
      const sessions = sessionsResponse.data?.results || [];
      if (sessions.length === 1 && sessions[0]?.id) {
        actualSessionId = sessions[0].id;
      }
    }
    if (!actualSessionId) {
      throw new Error('ambiguous-session');
    }
    await api.updateAttendanceSession(recordId, actualSessionId, { work_site_id: selectedSite.id });
  };

  const handleSiteSave = async (record, selectedSite, sessionIdOrIndex = null) => {
    if (!selectedSite || !selectedEmployee) return;
    try {
      setSavingCell(true);
      await persistSiteOnRecord(record, selectedSite, sessionIdOrIndex);

      setEditingSiteCell(null);
      setSiteSearchTerm('');
      const refreshedRecords = await loadEmployeeDailyRecords(selectedEmployee);
      const refreshedRecord = refreshedRecords?.find((rec) => rec.date === record.date);
      const assignmentIsVisible = refreshedRecord?.sites?.some(
        (site) => String(site.work_site_id) === String(selectedSite.id)
      );
      if (!assignmentIsVisible) {
        toast.error(
          `Site save for ${formatDate(record.date)} could not be verified. Refresh and review the row before locking payroll.`
        );
        return;
      }

      toast.success('Site assigned');
    } catch (error) {
      console.error('Error updating site:', error);
      if (error?.message === 'no-attendance-record') {
        toast.error('Cannot change site: no attendance record for this day (leave/rest day)');
      } else if (error?.message === 'ambiguous-session') {
        toast.error('Cannot change site: session context is ambiguous. Please edit the specific session row.');
      } else {
        toast.error(error.response?.data?.error || 'Failed to update site');
      }
      setEditingSiteCell(null);
      setSiteSearchTerm('');
    } finally {
      setSavingCell(false);
    }
  };

  // ── Multi-Select ───────────────────────────────────────────────────────────

  const handleRowSelect = (rec, index, event) => {
    if (rec.isPreEmployment || rec.isPostEmployment) return;
    const date = rec.date;
    const newSelected = new Set(selectedRows);
    if (event.shiftKey && lastSelectedIndex !== null) {
      const start = Math.min(lastSelectedIndex, index);
      const end = Math.max(lastSelectedIndex, index);
      for (let i = start; i <= end; i++) {
        const r = employeeDailyRecords[i];
        if (r && !r.isPreEmployment && !r.isPostEmployment) {
          newSelected.add(r.date);
        }
      }
    } else if (event.ctrlKey || event.metaKey) {
      newSelected.has(date) ? newSelected.delete(date) : newSelected.add(date);
    } else {
      newSelected.has(date) ? newSelected.delete(date) : newSelected.add(date);
    }
    setSelectedRows(newSelected);
    setLastSelectedIndex(index);
  };

  const handleSelectAll = () => {
    const selectableRecords = employeeDailyRecords.filter(r => !r.isPreEmployment && !r.isPostEmployment);
    const allSelected = selectableRecords.length > 0 && selectableRecords.every(r => selectedRows.has(r.date));
    if (allSelected) {
      setSelectedRows(new Set());
    } else {
      const newSelected = new Set();
      selectableRecords.forEach(r => newSelected.add(r.date));
      setSelectedRows(newSelected);
    }
  };

  // ── Bulk Lock / Unlock ─────────────────────────────────────────────────────

  const handleBulkLock = async () => {
    if (!selectedEmployee || selectedRows.size === 0) return;
    toast.dismiss('normalization-result');
    const datesToLock = Array.from(selectedRows).filter(date => {
      const record = employeeDailyRecords.find(r => r.date === date);
      return record && record.lock_status !== 'locked' && isDayLockableNow(record, todayIso);
    });
    const skippedIncomplete = Array.from(selectedRows).filter(d => {
      const record = employeeDailyRecords.find(r => r.date === d);
      return record && record.lock_status !== 'locked' && !isDayLockableNow(record, todayIso);
    }).length;
    if (skippedIncomplete > 0) {
      toast.warning(`Skipped ${skippedIncomplete} date(s) — only concluded days can be locked. Today is lockable when the employee is on approved full-day leave.`);
    }
    if (datesToLock.length === 0) {
      toast.info('No lockable dates selected (all locked or in the future)');
      return;
    }
    try {
      setRowLocking(new Set(datesToLock));
      const results = await Promise.allSettled(
        datesToLock.map(date =>
          api.post('/attendance-lock/apply/', {
            staff_id: selectedEmployee.staff_id, start_date: date, end_date: date,
          })
        )
      );
      const successDates = new Set(
        datesToLock.filter((_, i) => results[i].status === 'fulfilled')
      );
      const successCount = successDates.size;
      const failCount = results.filter(r => r.status === 'rejected').length;
      if (successCount > 0) {
        const updatedRecords = employeeDailyRecords.map(r =>
          successDates.has(r.date) ? { ...r, lock_status: 'locked' } : r
        );
        setEmployeeDailyRecords(updatedRecords);
        const allNowLocked = updatedRecords.filter(r => !r.isPreEmployment && !r.isPostEmployment).every(r => r.lock_status === 'locked');
        if (allNowLocked && period) {
          const { startIso, endIso } = getMonthRange(period);
          try {
            await api.post('/attendance-lock/apply/', {
              staff_id: selectedEmployee.staff_id, start_date: startIso, end_date: getLockableEndIso(endIso),
            });
            toastBulkDateResult({ successCount, failCount, autoLocked: true });
          } catch (autoLockError) {
            console.warn('Auto-lock failed:', autoLockError);
            toastBulkDateResult({ successCount, failCount, autoLocked: false });
          }
        } else {
          toastBulkDateResult({ successCount, failCount, autoLocked: false });
        }
        await Promise.all([
          fetchLocksSummary(), fetchGlobalLocksSummary(), fetchProcessingReadiness(), fetchAttendanceLocks(),
          buildEmployeeList(), loadEmployeeDailyRecords(selectedEmployee),
        ]);
      } else {
        toastBulkDateResult({ successCount: 0, failCount });
      }
      setSelectedRows(new Set());
      setLastSelectedIndex(null);
    } catch (error) {
      toast.error('Failed to lock selected rows');
    } finally {
      setRowLocking(new Set());
    }
  };

  const handleBulkUnlock = async () => {
    if (!selectedEmployee || selectedRows.size === 0) return;
    toast.dismiss('normalization-result');
    const datesToUnlock = Array.from(selectedRows).filter(date => {
      const record = employeeDailyRecords.find(r => r.date === date);
      return record && record.lock_status === 'locked' && !record.isPreEmployment && !record.isPostEmployment;
    });
    if (datesToUnlock.length === 0) {
      toast.info('All selected dates are already unlocked');
      return;
    }
    try {
      setRowLocking(new Set(datesToUnlock));
      const results = await Promise.allSettled(
        datesToUnlock.map(date =>
          api.post('/attendance-lock/remove/', {
            staff_id: selectedEmployee.staff_id, start_date: date, end_date: date,
          })
        )
      );
      const successDates = new Set(
        datesToUnlock.filter((_, i) => results[i].status === 'fulfilled')
      );
      const successCount = successDates.size;
      const failCount = results.filter(r => r.status === 'rejected').length;
      if (successCount > 0) {
        setEmployeeDailyRecords(rows =>
          rows.map(r => successDates.has(r.date) ? { ...r, lock_status: 'unlocked' } : r)
        );
        await Promise.all([
          fetchLocksSummary(), fetchGlobalLocksSummary(), fetchProcessingReadiness(), fetchAttendanceLocks(),
          buildEmployeeList(), loadEmployeeDailyRecords(selectedEmployee),
        ]);
        toastBulkDateResult({ successCount, failCount, action: 'unlock' });
      } else {
        toastBulkDateResult({ successCount: 0, failCount, action: 'unlock' });
      }
      setSelectedRows(new Set());
      setLastSelectedIndex(null);
    } catch (error) {
      toast.error('Failed to unlock selected rows');
    } finally {
      setRowLocking(new Set());
    }
  };

  // ── Row Lock Toggle ────────────────────────────────────────────────────────

  const handleRowLockToggle = async (rec) => {
    if (!selectedEmployee) return;
    toast.dismiss('normalization-result');
    if (rec.isPreEmployment || rec.isPostEmployment) return;
    if (rowLocking.has(rec.date)) return;
    if (rec.lock_status !== 'locked' && !isDayLockableNow(rec, todayIso)) {
      toast.error(getLockBlockReason(rec, todayIso));
      return;
    }
    const isLocked = rec.lock_status === 'locked';
    const body = { staff_id: selectedEmployee.staff_id, start_date: rec.date, end_date: rec.date };
    const prevUnlockedDates = unlockedDatesMap[selectedEmployee.staff_id] || [];
    try {
      setRowLocking(prev => new Set(prev).add(rec.date));
      const updatedRecords = employeeDailyRecords.map(r =>
        r.date === rec.date ? { ...r, lock_status: isLocked ? 'unlocked' : 'locked' } : r
      );
      setEmployeeDailyRecords(updatedRecords);

      if (isLocked) {
        await api.post('/attendance-lock/remove/', body);
        if (!prevUnlockedDates.includes(rec.date)) {
          setUnlockedDatesMap(prev => ({
            ...prev,
            [selectedEmployee.staff_id]: [...(prev[selectedEmployee.staff_id] || []), rec.date],
          }));
        }
      } else {
        await api.post('/attendance-lock/apply/', body);
        if (prevUnlockedDates.includes(rec.date)) {
          setUnlockedDatesMap(prev => ({
            ...prev,
            [selectedEmployee.staff_id]: (prev[selectedEmployee.staff_id] || []).filter(d => d !== rec.date),
          }));
        }
        const allNowLocked = updatedRecords.filter(r => !r.isPreEmployment && !r.isPostEmployment).every(r => r.lock_status === 'locked');
        if (allNowLocked && period) {
          const { startIso, endIso } = getMonthRange(period);
          try {
            await api.post('/attendance-lock/apply/', {
              staff_id: selectedEmployee.staff_id, start_date: startIso, end_date: getLockableEndIso(endIso),
            });
            toast.success('Employee locked for this period', { id: 'row-lock-toggle' });
          } catch (autoLockError) {
            console.warn('Auto-lock failed:', autoLockError);
          }
        }
      }

      await Promise.all([
        fetchLocksSummary(), fetchGlobalLocksSummary(), fetchProcessingReadiness(), fetchAttendanceLocks(),
        buildEmployeeList(), loadEmployeeDailyRecords(selectedEmployee),
      ]);
      if (!isLocked) {
        const allNowLocked = updatedRecords.filter(r => !r.isPreEmployment && !r.isPostEmployment).every(r => r.lock_status === 'locked');
        if (!allNowLocked) toast.success('Date locked', { id: 'row-lock-toggle' });
      } else {
        toast.success('Date unlocked', { id: 'row-lock-toggle' });
      }
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to toggle lock');
      setEmployeeDailyRecords(rows =>
        rows.map(r => r.date === rec.date ? { ...r, lock_status: isLocked ? 'locked' : 'unlocked' } : r)
      );
      setUnlockedDatesMap(prev => ({
        ...prev,
        [selectedEmployee.staff_id]: prevUnlockedDates,
      }));
    } finally {
      setRowLocking(prev => {
        const next = new Set(prev);
        next.delete(rec.date);
        return next;
      });
    }
  };

  // ── Per-row Re-normalize (single worker, single date) ───────────────────────
  // Lets HR recompute just one day for the selected worker after fixing that
  // day's clock data / sessions — instead of re-running the whole month.
  //
  // This routes through the SAME canonical pipeline as the bulk "Adjust"
  // button (`normalize_and_calculate_costs` via /labour-cost/normalize/),
  // scoped to one staff + one date, so results are guaranteed identical to a
  // full re-run for that day (leave-session refresh, AR-status refresh,
  // locked-pair skipping all included). We deliberately do NOT use the
  // lower-level /renormalize/ endpoint (process_staff_day_events) — it skips
  // leave-session materialisation and diverges on cross-midnight days.
  //
  // A lightweight inline poll keeps the row spinner going without opening the
  // full-screen progress modal (overkill for a single-day run).
  const handleRowRenormalize = async (rec) => {
    if (!selectedEmployee || !rec) return;
    if (rec.isPreEmployment || rec.isPostEmployment) return;
    if (rowRenormalizing.has(rec.date)) return;
    if (rec.lock_status === 'locked') {
      toast.error('This date is locked — unlock it before re-normalizing.');
      return;
    }
    try {
      setRowRenormalizing(prev => new Set(prev).add(rec.date));

      const res = await api.post('/labour-cost/normalize/', {
        start_date: rec.date,
        end_date: rec.date,
        department_ids: null,
        staff_ids: [selectedEmployee.staff_id],
      });

      const taskId = res.data?.task_id;
      if (taskId) {
        const POLL_INTERVAL = 800;
        const MAX_POLLS = 75; // ~1 minute ceiling for a single day
        for (let i = 0; i < MAX_POLLS; i++) {
          const sres = await api.get('/labour-cost/normalize/status/', { params: { task_id: taskId } });
          const st = sres.data?.state;
          if (st === 'SUCCESS') break;
          if (st === 'FAILURE') throw new Error(sres.data?.error || 'Normalization task failed');
          await new Promise(r => setTimeout(r, POLL_INTERVAL));
        }
      }

      await loadEmployeeDailyRecords(selectedEmployee);
      await Promise.all([fetchLocksSummary(), fetchGlobalLocksSummary()]);
      toast.success(`Re-normalized ${formatDateDisplay(rec.date)}`, { id: 'row-renormalize' });
    } catch (e) {
      toast.error('Failed to re-normalize this date.', { id: 'row-renormalize' });
      console.error('Row re-normalize error:', e);
    } finally {
      setRowRenormalizing(prev => {
        const next = new Set(prev);
        next.delete(rec.date);
        return next;
      });
    }
  };

  // ── Employee Lock Toggle ───────────────────────────────────────────────────

  const handleToggleEmployeeLock = async (emp) => {
    if (!period) return;
    toast.dismiss('normalization-result');
    const { startIso, endIso } = getMonthRange(period);
    const currentlyLocked = (selectedEmployee?.staff_id === emp.staff_id)
      ? isEmployeeFullyLocked(selectedEmployee, employeeDailyRecords)
      : (emp.lock_status === 'locked');

    if (!currentlyLocked && selectedEmployee?.staff_id === emp.staff_id) {
      if (!canLockEmployee(employeeDailyRecords)) {
        toast.error('Cannot lock: Some working days have incomplete data (missing clock times, hours, or site assignment)');
        return;
      }
    }
    try {
      if (currentlyLocked) {
        await api.post('/attendance-lock/remove/', {
          staff_id: emp.staff_id, start_date: startIso, end_date: endIso,
        });
        toast.success('Employee unlocked', { id: 'employee-lock-toggle' });
      } else {
        await api.post('/attendance-lock/apply/', {
          staff_id: emp.staff_id, start_date: startIso, end_date: getLockableEndIso(endIso),
        });
        toast.success('Employee locked for this period', { id: 'employee-lock-toggle' });
      }
      if (selectedEmployee?.staff_id === emp.staff_id) {
        const newStatus = currentlyLocked ? 'unlocked' : 'locked';
        setEmployeeDailyRecords(rows => rows.map(r => ({ ...r, lock_status: newStatus })));
      }
      lockCountCacheRef.current[emp.staff_id] = currentlyLocked ? 0 : (emp.total_days || 31);
      await Promise.all([fetchLocksSummary(), fetchGlobalLocksSummary(), fetchProcessingReadiness(), fetchAttendanceLocks(), buildEmployeeList()]);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to toggle');
    }
  };

  // ── Delete Session ─────────────────────────────────────────────────────────

  const handleDeleteSiteSession = (rec, site) => {
    setConfirmAction({ rec, site });
  };

  const handleConfirmDeleteSession = async () => {
    if (!confirmAction) return;
    const { rec, site } = confirmAction;
    const sessionId = site.id;
    try {
      if (sessionId && rec.attendance_record_id) {
        await api.setAttendanceSessionDeleted(rec.attendance_record_id, sessionId, true);
        toast.success('Session deleted');
      } else {
        const clockInEventId = site.clock_in_event_id;
        const clockOutEventId = site.clock_out_event_id;
        if (!clockInEventId && !clockOutEventId) {
          toast.error('Cannot delete: Session or event IDs not found');
          return;
        }
        const deletes = [];
        if (clockInEventId) deletes.push(api.deleteClockEvent(clockInEventId));
        if (clockOutEventId) deletes.push(api.deleteClockEvent(clockOutEventId));
        await Promise.all(deletes);
        if (selectedEmployee?.staff_id && rec?.date) {
          try { await api.renormalizeAttendance(selectedEmployee.staff_id, rec.date); } catch { /* ignore */ }
        }
        toast.success('Session deleted');
      }
      await loadEmployeeDailyRecords(selectedEmployee);
    } catch (e) {
      toast.error('Failed to delete session');
      console.error('Delete error:', e);
    } finally {
      setConfirmAction(null);
    }
  };

  // ── Normalize & Load ───────────────────────────────────────────────────────

  /**
   * Run attendance normalization, fetch data, and build the employee list.
   * @param {Function} [onComplete] - Called with { employeeList, unlockedDatesMap }
   *   after successful normalization. Use this for step transitions in the parent.
   */
  const _pollNormalizationTask = async (taskId) => {
    const POLL_INTERVAL = 1000;
    const MAX_POLLS = 600; // 10 minutes max
    for (let i = 0; i < MAX_POLLS; i++) {
      const res = await api.get('/labour-cost/normalize/status/', { params: { task_id: taskId } });
      const { state, progress, result, error } = res.data;

      if (state === 'PROGRESS' && progress) {
        setProgressModal(prev => ({
          ...prev,
          progress: progress.total > 0 ? { current: progress.current, total: progress.total } : null,
          statusMessage: progress.message || 'Normalizing...',
          department: progress.department || prev.department || null,
          currentEmployee: progress.current_employee || null,
          subPhase: progress.sub_phase || null,
        }));
      } else if (state === 'SUCCESS') {
        return result;
      } else if (state === 'FAILURE') {
        throw new Error(error || 'Normalization task failed');
      } else if (state === 'PENDING') {
        setProgressModal(prev => ({
          ...prev,
          progress: null,
          statusMessage: i < 3 ? 'Starting attendance normalization...' : 'Waiting for background worker to pick up task...',
        }));
      }
      await new Promise(r => setTimeout(r, POLL_INTERVAL));
    }
    throw new Error('Normalization timed out after 10 minutes');
  };

  /**
   * Shared Phase 1–3: fetch records/locks, build employee list, load calendar.
   * Used by both handleAdjust (full normalize + load) and handleLoadOnly (load only).
   */
  const _loadAndBuildEmployeeList = async (callback) => {
    const gen = ++rosterLoadGenRef.current;
    const { start, end, startIso, endIso } = getMonthRange(period);

    // Phase 1: Fetch summary data
    setProgressModal(prev => ({
      ...prev, currentPhase: 1, progress: null,
      statusMessage: 'Loading attendance records and locks...',
    }));

    setAdjustedReady(true);
    const [, , records, locks] = await Promise.all([
      fetchLocksSummary(period, _deptIds),
      fetchGlobalLocksSummary(period),
      fetchAttendanceRecords(period, _deptIds),
      fetchAttendanceLocks(period, _deptIds),
    ]);

    // A newer pill click owns the modal and the name list. Do not close
    // the modal or write employees from this superseded load.
    if (gen !== rosterLoadGenRef.current || !Array.isArray(records)) return;

    const currentEmployeeList = await buildEmployeeList(records, locks || {}, gen);
    if (gen !== rosterLoadGenRef.current) return;
    if (currentEmployeeList == null) {
      setProgressModal(prev => ({ ...prev, open: false }));
      return;
    }
    if (currentEmployeeList.length === 0) {
      setProgressModal(prev => ({ ...prev, open: false }));
      toast.warning('No clock events found for employees in this department for the selected period.');
      return;
    }

    // Phase 2: Load employee calendar data
    // The denominator on each `emp` was already computed in
    // `buildEmployeeList` from the employment window (server-supplied
    // `effective_days_in_period` with a local fallback) — reuse that
    // value instead of re-deriving from the calendar month, which would
    // re-introduce the mid-period-joiner / mid-period-leaver bug for the
    // locked-employees fast path that skips the calendar fetch.
    const getEffectiveDays = (emp) => {
      if (emp && typeof emp.total_days === 'number' && emp.total_days >= 0) {
        return emp.total_days;
      }
      // Hard fallback (should not be reached in practice — employees in
      // currentEmployeeList always carry total_days from buildEmployeeList).
      return computeEmploymentWindow(period, emp?.employment_start_date, emp?.employment_end_date).effectiveDays;
    };

    // Separate locked employees (skip calendar fetch) from unlocked ones.
    // Use emp.lock_status (derived from backend locked_count via date-union of
    // AttendanceLock records) rather than the raw locks map. The raw map is built
    // with a simple forEach that has no ordering guarantee — the last record for
    // an employee wins non-deterministically when multiple AttendanceLock rows
    // exist (e.g. stale period-level lock + individual-day unlocks), causing
    // fully-locked status to be falsely reported after renormalization.
    const lockedEmployees = [];
    const unlockedEmployees = [];
    for (const emp of currentEmployeeList) {
      if (emp.lock_status === 'locked') {
        lockedEmployees.push(emp);
      } else {
        unlockedEmployees.push(emp);
      }
    }

    const totalEmps = currentEmployeeList.length;
    setProgressModal(prev => ({
      ...prev, currentPhase: 2,
      progress: { current: lockedEmployees.length, total: totalEmps },
      statusMessage: lockedEmployees.length > 0
        ? `Skipped ${lockedEmployees.length} fully-locked employees...`
        : '',
    }));

    const allEmployeesWithStatus = [];
    const newUnlockedDatesMap = {};

    // Add locked employees immediately without HTTP calls
    for (const emp of lockedEmployees) {
      const effectiveDays = getEffectiveDays(emp);
      allEmployeesWithStatus.push({
        ...emp,
        unlockedCount: 0,
        totalCount: effectiveDays,
        locked_count: effectiveDays,
        total_days: effectiveDays,
        isFullyLocked: true,
      });
    }

    // Fetch calendar data for unlocked employees via bulk endpoint
    if (unlockedEmployees.length > 0) {
      const BATCH_SIZE = 20;
      let fetched = 0;
      for (let batchStart = 0; batchStart < unlockedEmployees.length; batchStart += BATCH_SIZE) {
        if (gen !== rosterLoadGenRef.current) return;
        const batch = unlockedEmployees.slice(batchStart, batchStart + BATCH_SIZE);
        const batchStaffIds = batch.map(e => e.staff_id);

        setProgressModal(prev => ({
          ...prev,
          progress: { current: lockedEmployees.length + fetched, total: totalEmps },
          statusMessage: `Loading calendar data (${fetched + 1}-${Math.min(fetched + batch.length, unlockedEmployees.length)} of ${unlockedEmployees.length} unlocked)...`,
        }));

        try {
          const bulkRes = await api.post('/labour-cost/attendance-calendar/bulk/', {
            staff_ids: batchStaffIds,
            start_date: startIso,
            end_date: endIso,
          });
          const bulkData = bulkRes.data?.data || {};

          for (const emp of batch) {
            const calData = bulkData[emp.staff_id];
            const dailyRecords = calData?.records || [];
            const empStartIso = emp.employment_start_date || null;
            const empEndIso = emp.employment_end_date || employmentEndDates[emp.staff_id] || null;

            // Exclude pre/post-employment placeholder rows from both the
            // unlocked-date list and the locked-count tally — the
            // calendar endpoint emits one row per day with is_pre_employment
            // / is_post_employment flags; once filtered, `inRangeRecords.length`
            // IS the exact denominator (one row per employed day in period).
            const isWithinEmployment = (rec) => {
              if (rec.is_pre_employment || rec.is_post_employment) return false;
              if (empStartIso && rec.date && rec.date < empStartIso) return false;
              if (empEndIso && rec.date && rec.date > empEndIso) return false;
              return true;
            };
            const inRangeRecords = dailyRecords.filter(isWithinEmployment);
            // Prefer the count of in-window calendar rows (always exact)
            // over the buildEmployeeList denominator. They should agree;
            // if they don't, the calendar is the source of truth.
            const effectiveDays = inRangeRecords.length > 0
              ? inRangeRecords.length
              : getEffectiveDays(emp);
            const empUnlockedDates = inRangeRecords
              .filter(rec => rec.lock_status !== 'locked')
              .map(rec => rec.date);
            let lockedCount = inRangeRecords.length - empUnlockedDates.length;
            lockedCount = Math.min(Math.max(lockedCount, 0), effectiveDays);

            allEmployeesWithStatus.push({
              ...emp,
              unlockedCount: empUnlockedDates.length,
              totalCount: inRangeRecords.length,
              locked_count: lockedCount,
              total_days: effectiveDays,
              isFullyLocked: empUnlockedDates.length === 0 && inRangeRecords.length > 0,
            });
            if (empUnlockedDates.length > 0) {
              newUnlockedDatesMap[emp.staff_id] = empUnlockedDates;
            }
          }
        } catch (error) {
          console.error(`Error fetching bulk calendar for batch starting at ${batchStart}:`, error);
          for (const emp of batch) {
            const effectiveDays = getEffectiveDays(emp);
            allEmployeesWithStatus.push({
              ...emp,
              unlockedCount: 0,
              totalCount: effectiveDays,
              locked_count: 0,
              total_days: effectiveDays,
              isFullyLocked: false,
            });
          }
        }
        fetched += batch.length;
      }
    }

    // Superseded by a newer department load. Leave its modal and roster alone.
    if (gen !== rosterLoadGenRef.current) return;

    // Phase 3: Finalizing
    setProgressModal(prev => ({
      ...prev, currentPhase: 3, progress: null,
      statusMessage: 'Preparing review screen...',
    }));

    setUnlockedDatesMap(newUnlockedDatesMap);

    if (Object.keys(employmentEndDates).length > 0) {
      allEmployeesWithStatus.sort((a, b) => {
        const endA = employmentEndDates[a.staff_id] || '9999-12-31';
        const endB = employmentEndDates[b.staff_id] || '9999-12-31';
        return endA.localeCompare(endB);
      });
    }

    if (gen !== rosterLoadGenRef.current) return;
    setEmployeeList(allEmployeesWithStatus);
    if (allEmployeesWithStatus.length > 0) {
      setSelectedEmployee(resolvePersistedSelection(allEmployeesWithStatus));
    }

    setProgressModal(prev => ({ ...prev, open: false }));

    if (callback) {
      callback({
        employeeList: allEmployeesWithStatus,
        unlockedDatesMap: newUnlockedDatesMap,
      });
    }

    return { allEmployeesWithStatus, newUnlockedDatesMap };
  };

  /**
   * Full normalize + load. Used for Step 1→2 transition and explicit re-normalize.
   */
  const handleAdjust = async (onComplete) => {
    const callback = typeof onComplete === 'function' ? onComplete : null;
    if (!period) return;

    const { start, end, startIso, endIso } = getMonthRange(period);

    setProgressModal({
      open: true, currentPhase: 0, progress: null,
      statusMessage: 'Sending normalization request...', error: null,
      department: null, currentEmployee: null, subPhase: null,
    });
    setAdjusting(true);
    lockCountCacheRef.current = {};
    setEmployeeDailyRecords([]);

    try {
      // Phase 0: Normalize (async background task).
      // Do not clamp today away here — the backend persists only concluded
      // final employment sessions on the same day, and still skips open shifts.
      const normalizePayload = {
        start_date: toLocalISODate(start),
        end_date: endIso,
        department_ids: _deptIds.length > 0 ? _deptIds : null,
      };
      if (staffIdFilter !== null && staffIdFilter !== undefined) {
        if (staffIdFilter.length > 0) {
          normalizePayload.staff_ids = staffIdFilter;
        } else {
          setProgressModal(prev => ({ ...prev, open: false }));
          toast.warning('No employees found for the selected trade filter.');
          return;
        }
      }

      const normalizeRes = await api.post('/labour-cost/normalize/', normalizePayload);
      const taskId = normalizeRes.data?.task_id;

      if (taskId) {
        setProgressModal(prev => ({
          ...prev, statusMessage: 'Normalizing attendance records...',
        }));
        await _pollNormalizationTask(taskId);
      }

      const result = await _loadAndBuildEmployeeList(callback);

      const totalUnlocked = result
        ? Object.values(result.newUnlockedDatesMap).reduce((sum, dates) => sum + dates.length, 0)
        : 0;
      if (totalUnlocked > 0) {
        toast.success(
          `Attendance ready — ${formatDateCount(totalUnlocked)} still unlocked`,
          { id: 'normalization-result' }
        );
      } else if (result) {
        toast.success('Attendance ready — all dates locked', { id: 'normalization-result' });
      }
    } catch (e) {
      console.error('Normalization error:', e);
      const errorMessage =
        e?.response?.data?.error || e?.message || 'An unexpected error occurred during normalization.';
      setProgressModal(prev => ({ ...prev, error: errorMessage, statusMessage: '' }));
    } finally {
      setAdjusting(false);
    }
  };

  /**
   * Load-only (no normalization). Used for department switches within Step 2 —
   * data was already normalized in Step 1, so re-running the Celery task is
   * wasteful and forces HR to wait unnecessarily.
   */
  const handleLoadOnly = async (onComplete) => {
    const callback = typeof onComplete === 'function' ? onComplete : null;
    if (!period) return;

    setProgressModal({
      open: true, currentPhase: 1, progress: null,
      statusMessage: 'Loading department data...', error: null,
    });
    setAdjusting(true);
    lockCountCacheRef.current = {};
    setEmployeeDailyRecords([]);

    try {
      await _loadAndBuildEmployeeList(callback);
    } catch (e) {
      console.error('Load error:', e);
      const errorMessage =
        e?.response?.data?.error || e?.message || 'An unexpected error occurred while loading data.';
      setProgressModal(prev => ({ ...prev, error: errorMessage, statusMessage: '' }));
    } finally {
      setAdjusting(false);
    }
  };

  // ── Effects ────────────────────────────────────────────────────────────────

  // Auto-load daily records when employee is selected
  useEffect(() => {
    if (enabled && selectedEmployee && period && adjustedReady) {
      if (employeeDailyRecords.length === 0 || lastLoadedPeriodRef.current !== period) {
        lastLoadedPeriodRef.current = period;
        loadEmployeeDailyRecords(selectedEmployee);
      }
    }
  }, [enabled, selectedEmployee, period, adjustedReady]);

  // Restore / validate the selected employee whenever the list is (re)built.
  // This is the single authority for "who is selected":
  //   • hard refresh — the list rebuilds with no live selection, so we restore
  //     the persisted employee for this context (the fix for "bring me back to
  //     S0060"). Without this the detail panel would come back blank because
  //     the department-switch reset nulls the selection before the list loads.
  //   • current selection still valid — leave it untouched (no churn).
  //   • current selection dropped out of the list — warn HR and fall back to
  //     the persisted-or-first employee instead of stranding them on a blank
  //     panel.
  useEffect(() => {
    if (!enabled || employeeList.length === 0) return;

    const stillValid = selectedEmployee
      && employeeList.some(e => e.staff_id === selectedEmployee.staff_id);
    if (stillValid) return;

    const restored = resolvePersistedSelection(employeeList);
    if (selectedEmployee && (!restored || restored.staff_id !== selectedEmployee.staff_id)) {
      toast.warning('Previously selected employee no longer available.', { icon: '⚠️' });
    }
    setSelectedEmployee(restored);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeList, selectedEmployee, enabled]);

  // Restore unsaved edits notification
  useEffect(() => {
    if (selectedEmployee && enabled) {
      const contextEdits = getCurrentContextEdits();
      const editCount = Object.keys(contextEdits).length;
      if (editCount > 0) {
        toast(`Restored ${editCount} unsaved edit(s) for this employee`, { id: 'restored-edits' });
      }
    }
  }, [selectedEmployee, enabled]);

  // Clear selection when switching employees
  useEffect(() => {
    if (selectedEmployee) {
      setSelectedRows(new Set());
      setLastSelectedIndex(null);
    }
  }, [selectedEmployee?.staff_id]);

  // Before-unload warning for unsaved edits
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedEdits) {
        e.preventDefault();
        e.returnValue = 'You have unsaved edits. Are you sure you want to leave?';
        return e.returnValue;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedEdits]);

  // Close site dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (editingSiteCell && !event.target.closest('.site-dropdown-container')) {
        setEditingSiteCell(null);
        setSiteSearchTerm('');
      }
    };
    if (editingSiteCell) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [editingSiteCell]);

  // Fetch exceptions/locks summary when period or department changes;
  // also fetch attendance records/locks when adjustedReady
  useEffect(() => {
    if (!period) return;
    recheckExceptions(false);
    fetchLocksSummary();
    fetchGlobalLocksSummary();
    fetchProcessingReadiness();
    if (adjustedReady) {
      fetchAttendanceRecords();
      fetchAttendanceLocks();
    }
  }, [period, _deptIds.join(','), adjustedReady]);

  // Department change resets normalized state (including selection to prevent
  // the validation effect from toasting a false "no longer available" warning).
  // Also drops in-flight roster writes from the previous pill, and clears the
  // name-list search / classification lens. Those are scoped to the department
  // HR was just looking at; carrying "Worker" or a typed name onto a
  // department that has neither leaves the panel looking empty, and a refresh
  // (which resets this state) is the only way the names come back.
  const _deptIdsKey = _deptIds.join(',');
  useEffect(() => {
    rosterLoadGenRef.current += 1;
    setAttendanceRecords([]);
    setLocksByStaffId({});
    setSelectedEmployee(null);
    setAdjustedReady(false);
    setEmployeeSearchTerm('');
    setFilterClassification('all');
    lockCountCacheRef.current = {};
  }, [_deptIdsKey]);

  // Build employee list when records or locks change
  useEffect(() => {
    if (attendanceRecords.length > 0) {
      buildEmployeeList();
    }
  }, [attendanceRecords, locksByStaffId, employeeDailyRecords]);

  // ── Derived State ──────────────────────────────────────────────────────────

  const lockedAll = locksSummary.total > 0 && locksSummary.locked === locksSummary.total;
  const globalLockedAll = globalLocksSummary.total > 0 && globalLocksSummary.locked === globalLocksSummary.total;

  // Gate: proceed if EITHER category is ready (from processing-readiness endpoint)
  // Falls back to the simple lock-based check when endpoint data isn't loaded yet
  const canProceedToCalculation = processingReadiness
    ? (processingReadiness.staff?.can_proceed || processingReadiness.workmen?.can_proceed)
    : (globalLocksSummary.total > 0 && globalLocksSummary.locked > 0);
  const hasExceptionsWarning = exceptionsPending > 0;

  // ── Convenience: all AdjustAndLockTable props ──────────────────────────────

  const getTableProps = () => ({
    employeeList,
    selectedEmployee,
    employeeDailyRecords,
    periodLabel: formatPeriodRange(period),
    onRefresh: handleAdjust,
    isRefreshing: adjusting,
    canRefresh: !!period,
    conflictWarning,
    onDismissConflict: () => setConflictWarning(null),
    hasUnsavedEdits,
    onSelectEmployee: selectEmployee,
    onToggleEmployeeLock: handleToggleEmployeeLock,
    selectedRows,
    setSelectedRows,
    lastSelectedIndex,
    setLastSelectedIndex,
    expandedRows,
    onToggleRow: toggleRow,
    editingCell,
    setEditingCell,
    cellValue,
    setCellValue,
    savingCell,
    editingSiteCell,
    setEditingSiteCell,
    siteSearchTerm,
    setSiteSearchTerm,
    leftPanelCollapsed,
    setLeftPanelCollapsed,
    employeeSearchTerm,
    setEmployeeSearchTerm,
    filterClassification,
    setFilterClassification,
    onSelectAll: handleSelectAll,
    onRowSelect: handleRowSelect,
    onCellClick: handleCellClick,
    onCellSave: handleCellSave,
    onSessionCellSave: handleSessionCellSave,
    onSiteClick: handleSiteClick,
    onSiteSave: handleSiteSave,
    onRowLockToggle: handleRowLockToggle,
    onRowRenormalize: handleRowRenormalize,
    rowRenormalizing,
    onBulkLock: handleBulkLock,
    onBulkUnlock: handleBulkUnlock,
    onClearSelection: () => { setSelectedRows(new Set()); setLastSelectedIndex(null); },
    onDeleteSession: handleDeleteSiteSession,
    getFilteredAndSortedSites,
    siteFrequencyMap,
    getSiteFrequency,
    isRowEditable,
    isEmployeeFullyLocked,
    canLockEmployee,
    rowLocking,
    todayIso,
    formatDateDisplay,
    formatDateTimeDisplay,
    deriveFromSites,
  });

  // ── Return ─────────────────────────────────────────────────────────────────

  return {
    // State
    exceptionsPending,
    locksSummary,
    globalLocksSummary,
    adjusting,
    totalEmployees,
    recordsLoading,
    attendanceRecords,
    locksLoading,
    locksByStaffId,
    adjustedReady, setAdjustedReady,
    employeeList, setEmployeeList,
    selectedEmployee, setSelectedEmployee,
    employeeDailyRecords, setEmployeeDailyRecords,
    rowLocking,
    rowRenormalizing,
    employeeLocking,
    employeeSearchTerm, setEmployeeSearchTerm,
    filterClassification, setFilterClassification,
    expandedRows,
    leftPanelCollapsed, setLeftPanelCollapsed,
    selectedRows, setSelectedRows,
    lastSelectedIndex, setLastSelectedIndex,
    progressModal, setProgressModal,
    ADJUST_PHASES,
    allEditedValues,
    hasUnsavedEdits,
    conflictWarning, setConflictWarning,
    editingCell, setEditingCell,
    cellValue, setCellValue,
    savingCell,
    editingSiteCell, setEditingSiteCell,
    siteSearchTerm, setSiteSearchTerm,
    workSites,
    siteFrequencyMap,
    confirmAction, setConfirmAction,
    unlockedDatesMap, setUnlockedDatesMap,

    // Derived
    lockedAll,
    globalLockedAll,
    canProceedToCalculation,
    hasExceptionsWarning,
    processingReadiness,
    todayIso,

    // Handlers
    recheckExceptions,
    fetchLocksSummary,
    fetchGlobalLocksSummary,
    fetchProcessingReadiness,
    fetchAttendanceLocks,
    fetchAttendanceRecords,
    handleApplyLock,
    handleRemoveLock,
    toggleRow,
    buildEmployeeList,
    loadEmployeeDailyRecords,
    selectEmployee,
    handleCellClick,
    handleCellSave,
    handleSessionCellSave,
    handleSiteClick,
    handleSiteSave,
    handleRowSelect,
    handleSelectAll,
    handleBulkLock,
    handleBulkUnlock,
    handleRowLockToggle,
    handleRowRenormalize,
    handleToggleEmployeeLock,
    handleDeleteSiteSession,
    handleConfirmDeleteSession,
    handleAdjust,
    handleLoadOnly,

    // Helpers
    isRowEditable,
    getFilteredAndSortedSites,
    getSiteFrequency,
    formatPeriodRange,
    formatDateDisplay,
    formatDateTimeDisplay,
    deriveFromSites,
    toLocalISODate,
    getMonthRange,

    // Convenience
    getTableProps,
  };
};
